import React from 'react';
import CommonButton from '../../Components/CommonButton';
import CommonLoader from '../../Components/CommonLoader';
import NoDataPage from '../../Components/NoDataPage';
import PageHeading from '../../Components/PageHeading';
import StatCard from '../../Components/StatCard';
import StatusBadge from '../../Components/StatusBadge';
import { useNova } from '../NovaContext';
import ErrorRow from '../components/ErrorRow';
import Glyph from '../components/Glyph';
import SeverityPill from '../components/SeverityPill';
import TabPanel from '../components/TabPanel';
import { T } from '../components/Text';
import { COMPLIANCE_PROMPT, INSIGHT_ACTIONS, MAX_QUESTION_LENGTH, POSTURE_PROMPT, RISKS_PROMPT } from '../constants';
import { IconChevronRight, IconRefresh } from '../icons';

const GRADE_VARIANT = { A: 'success', B: 'success', C: 'warning', D: 'danger', E: 'danger', F: 'danger' };

const fit = (prompt) => (prompt.length > MAX_QUESTION_LENGTH ? `${prompt.slice(0, MAX_QUESTION_LENGTH - 1)}…` : prompt);

const show = (value) => (value === null || value === undefined ? '-' : value.toLocaleString());

function Summary({ summary }) {
    const { grade, highRisks, assets } = summary;
    const gradeColor = { success: 'var(--color-success)', warning: 'var(--color-warning)', danger: 'var(--color-danger)' }[GRADE_VARIANT[grade]];

    return (
        <div className="nova_summary">
            <StatCard className="cmn_elevated_card" label="Security grade" value={grade || '-'} valueSize={24} valueColor={gradeColor} />
            <StatCard
                className="cmn_elevated_card"
                label="High risks"
                value={show(highRisks)}
                valueSize={24}
                valueColor={highRisks > 0 ? 'var(--color-danger)' : undefined}
            />
            <StatCard className="cmn_elevated_card" label="Assets" value={show(assets)} valueSize={24} />
        </div>
    );
}

function Card({ glyph, title, onViewAll, children }) {
    return (
        <div className="nova_card cmn_elevated_card">
            <div className="nova_card_head">
                <span className="nova_card_title">
                    <span className="nova_icon_box"><Glyph name={glyph} size={18} /></span>
                    <T as="h3" size="md" weight="semibold" className="nova_text_strong">{title}</T>
                </span>
                <button type="button" className="nova_link" onClick={onViewAll}>
                    <T size="base" weight="medium">Ask Foresee AI</T>
                </button>
            </div>
            {children}
        </div>
    );
}

function Unavailable({ what }) {
    return <T as="p" size="base" className="nova_muted nova_note">{what} could not be loaded.</T>;
}

function TopRisks({ risks }) {
    const { actions } = useNova();

    return (
        <Card glyph="risk" title="Top security risks" onViewAll={() => actions.send(RISKS_PROMPT)}>
            {risks === null && <Unavailable what="Risks" />}
            {risks && risks.top.length === 0 && (
                <NoDataPage title="No risks found" hint="Risks appear once a scan has completed." minHeight={120} />
            )}
            {risks && risks.top.length > 0 && (
                <ul className="nova_rows">
                    {risks.top.map((risk) => (
                        <li key={risk.id}>
                            <button type="button" className="nova_row nova_row--button" onClick={() => actions.send(fit(`Tell me about this risk: ${risk.title}`))}>
                                <span className="nova_row_badge"><SeverityPill severity={risk.severity} /></span>
                                <T size="md" weight="medium" className="nova_text_strong nova_clamp">{risk.title}</T>
                                <IconChevronRight size={16} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
}

function Posture({ scanned, categories }) {
    const { actions } = useNova();

    return (
        <Card glyph="easm" title="Security posture" onViewAll={() => actions.send(POSTURE_PROMPT)}>
            {categories === null && <Unavailable what="Scan results" />}
            {categories && (!scanned || categories.length === 0) && (
                <NoDataPage title="No scan results yet" hint="Your security grade appears once the first scan completes." minHeight={120} />
            )}
            {categories && scanned && categories.length > 0 && (
                <ul className="nova_rows">
                    {categories.map((item) => (
                        <li key={item.category}>
                            <button type="button" className="nova_row nova_row--button" onClick={() => actions.send(fit(`Tell me about my ${item.category} findings`))}>
                                <span className="nova_row_text">
                                    <T size="md" weight="semibold" className="nova_text_strong nova_ellipsis">{item.category}</T>
                                    <T size="base" className="nova_muted">{item.high} high · {item.medium} medium · {item.low} low</T>
                                </span>
                                {item.grade && <StatusBadge variant={GRADE_VARIANT[item.grade] || 'neutral'} dot={false} label={`Grade ${item.grade}`} transform="none" />}
                                <IconChevronRight size={16} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
}

function Compliance({ frameworks }) {
    const { actions } = useNova();

    return (
        <Card glyph="compliance" title="Compliance" onViewAll={() => actions.send(COMPLIANCE_PROMPT)}>
            {frameworks === null && <Unavailable what="Compliance" />}
            {frameworks && frameworks.length === 0 && (
                <NoDataPage title="No frameworks yet" hint="Add a compliance framework to track progress." minHeight={120} />
            )}
            {frameworks && frameworks.length > 0 && (
                <ul className="nova_rows">
                    {frameworks.map((item) => (
                        <li key={item.id} className="nova_row">
                            <span className="nova_row_text">
                                <T size="md" weight="semibold" className="nova_text_strong nova_ellipsis">{item.name}</T>
                                <T size="base" className="nova_muted">Responses {item.responses}%</T>
                            </span>
                            <T size="md" weight="medium" className="nova_text_strong">{item.compliant}% compliant</T>
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
}

export default function InsightsPage() {
    const { state, actions } = useNova();
    const { status, data } = state.insights;
    console.log("ffff", data)
    const loading = status === 'idle' || status === 'loading';

    return (
        <TabPanel id="insights">
            <div className="nova_page">
                <PageHeading
                    level={3}
                    title="Security insights"
                    subtitle="From your latest scan, risk register and compliance answers"
                    actions={(
                        <CommonButton
                            type="button"
                            variant="white"
                            className="nova_btn nova_btn--sm"
                            disable={loading}
                            onClick={actions.reloadInsights}
                            text={<><IconRefresh size={14} />Refresh</>}
                        />
                    )}
                />

                {status === 'error' && <ErrorRow message="Insights could not be loaded." onRetry={actions.reloadInsights} />}
                {loading && <div className="nova_loading cmn_elevated_card"><CommonLoader label="Loading insights" /></div>}
                {status === 'ready' && data && (
                    <>
                        <Summary summary={data.summary} />
                        <TopRisks risks={data.risks} />
                        <Posture scanned={data.scanned} categories={data.categories} />
                        {/* <Compliance frameworks={data.frameworks} /> */}
                        <div className="nova_actions_grid">
                            {INSIGHT_ACTIONS.map(({ label, glyph, prompt }) => (
                                <CommonButton
                                    key={label}
                                    type="button"
                                    variant="white"
                                    className="nova_btn nova_btn--wide"
                                    onClick={() => actions.send(prompt)}
                                    text={<><Glyph name={glyph} size={16} />{label}</>}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </TabPanel>
    );
}
